import React, { useRef } from "react";
import { Button, Divider, Tag } from "antd";
import { ArrowLeftOutlined, CalendarOutlined, EnvironmentOutlined, TeamOutlined } from "@ant-design/icons";
import { CompactTable } from "../../common/components/CompactTable";
import { TextWithIconPrefix } from "../../common/components/TextWithIconPrefix";
import { GoalieSelector } from "../../common/components/GoalieSelector";
import { AttendanceButtons } from "../../common/components/AttendanceButtons";
import { Attendance, Game, UserAuth } from "../../common/interfaces";
import { useGet, usePost } from "../../common/hooks";
import { formatISODate, getDivisionName, toModifyType } from "../../common/utils";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { GoalieAssignmentSeasonUrl } from "../routes";
import { useEnsuredUser } from "../../common/contexts";
import Spinner from "../../common/components/Spinner";

interface GoalieForGame {
  id: number;
  name: string;
  availability: Attendance;
  contact: string;
  assignedHome: boolean;
  assignedAway: boolean;
}

function sortGoalies(goalies: GoalieForGame[]): GoalieForGame[] {
  const order = (g: GoalieForGame) => {
    if (g.assignedHome || g.assignedAway) return 0;
    if (g.availability === Attendance.IN) return 1;
    if (g.availability === Attendance.UNRESPONDED) return 2;
    return 3;
  };
  return [...goalies].sort((a, b) => order(a) - order(b));
}

// Reorders live when a goalie is assigned/unassigned, but otherwise keeps each goalie at its
// initial-load position so clicking availability buttons doesn't jarringly reshuffle the table.
function sortGoaliesStably(goalies: GoalieForGame[], initialOrder: Map<number, number>): GoalieForGame[] {
  const assignedRank = (g: GoalieForGame) => (g.assignedHome || g.assignedAway ? 0 : 1);
  return [...goalies].sort((a, b) => {
    const assignedDiff = assignedRank(a) - assignedRank(b);
    if (assignedDiff !== 0) return assignedDiff;
    return (initialOrder.get(a.id) ?? 0) - (initialOrder.get(b.id) ?? 0);
  });
}

export function GoalieAssign() {
  const user = useEnsuredUser();
  const isCoordinator = user.authTypes.includes(UserAuth.GOALIE_COORDINATOR);
  const { gameId } = useParams();
  const { data: game, isLoading: gameLoading } = useGet<Game>(`/api/games?gameId=${gameId}`);
  const goaliesReq = useGet<GoalieForGame[]>(
    gameId ? `/api/goaliesForGame?gameId=${gameId}` : undefined
  );
  const assignReq = usePost({ uri: "/api/submitRoster", deps: [goaliesReq] });
  const availabilityReq = usePost({ uri: "/api/updateGoalieAvailability", deps: [goaliesReq] });
  const initialOrderRef = useRef<Map<number, number> | null>(null);

  if (gameLoading || goaliesReq.data === undefined || !game) {
    return <Spinner size="large" />;
  }

  if (initialOrderRef.current === null && goaliesReq.data) {
    initialOrderRef.current = new Map(sortGoalies(goaliesReq.data).map((g, i) => [g.id, i]));
  }

  const sorted = sortGoaliesStably(goaliesReq.data || [], initialOrderRef.current || new Map());

  const assign = (row: GoalieForGame | null, teamId: number, originalGoalieName: string | undefined) =>
    assignReq.send({
      goalie: row ? row.name : "",
      goalieId: row ? row.id : undefined,
      gameId,
      teamId,
      originalGoalieName
    });

  const onModifyAvailability = (row: GoalieForGame, modifyType: string) => {
    // Clicking the already-active button clears the entry (coordinator misclick safety net),
    // unlike a goalie's own availability page, which doesn't allow unselecting.
    const resolvedType = toModifyType(modifyType, row.availability);
    availabilityReq.send({
      gameId,
      goalieId: row.id,
      available: resolvedType === "" ? undefined : resolvedType === "add"
    });
  };

  const columns = [
    {
      key: "name",
      title: "Goalie",
      render: (_: any, row: GoalieForGame) => (
        <span>
          {row.name}
          {(row.assignedHome || row.assignedAway) && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {row.assignedHome ? "Home" : "Away"}
            </Tag>
          )}
        </span>
      )
    },
    {
      key: "availability",
      title: "Availability",
      render: (_: any, row: GoalieForGame) => (
        <AttendanceButtons
          attendance={row.availability}
          onClick={(modifyType: string) => onModifyAvailability(row, modifyType)}
          disabled={!isCoordinator || availabilityReq.isLoading}
        />
      )
    },
    {
      key: "contact",
      title: "Contact",
      dataIndex: "contact"
    },
    {
      key: "actions",
      title: "",
      render: (_: any, row: GoalieForGame) => {
        const isAssigned = row.assignedHome || row.assignedAway;
        return (
          <span style={{ display: "flex", gap: 8 }}>
            <Button
              size="small"
              danger={isAssigned && row.assignedHome}
              disabled={isAssigned && !row.assignedHome}
              loading={assignReq.isLoading}
              onClick={() => assign(
                row.assignedHome ? null : row,
                game.team1Id,
                row.assignedHome ? row.name : game.team1GoalieName
              )}
            >
              {isAssigned && row.assignedHome ? "Unassign - Home" : "Assign - Home"}
            </Button>
            <Button
              size="small"
              danger={isAssigned && row.assignedAway}
              disabled={isAssigned && !row.assignedAway}
              loading={assignReq.isLoading}
              onClick={() => assign(
                row.assignedAway ? null : row,
                game.team2Id,
                row.assignedAway ? row.name : game.team2GoalieName
              )}
            >
              {isAssigned && row.assignedAway ? "Unassign - Away" : "Assign - Away"}
            </Button>
          </span>
        );
      }
    }
  ];

  const backUrl = GoalieAssignmentSeasonUrl.createUrl(
    game.division !== undefined ? getDivisionName(game.division).toLowerCase() : undefined
  );

  return (
    <>
      <div style={{ paddingBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <TextWithIconPrefix icon={<TeamOutlined />} text={`${game.team1Name} (Home) vs ${game.team2Name} (Away)`} />
          <Link to={backUrl}>
            <Button icon={<ArrowLeftOutlined />}>Back to Games</Button>
          </Link>
        </div>
        <TextWithIconPrefix icon={<CalendarOutlined />} text={formatISODate(game.date)} />
        <TextWithIconPrefix icon={<EnvironmentOutlined />} text={game.rinkName} />
      </div>
      <Divider />
      <CompactTable data={sorted} columns={columns} loading={goaliesReq.isLoading} />
      <Divider>Override Assignment</Divider>
      <div style={{ display: "flex", gap: 24 }}>
        <div>
          <div style={{ marginBottom: 4 }}>{game.team1Name} (Home)</div>
          <GoalieSelector gameId={String(game.id)} teamId={String(game.team1Id)} showSubLabel />
        </div>
        <div>
          <div style={{ marginBottom: 4 }}>{game.team2Name} (Away)</div>
          <GoalieSelector gameId={String(game.id)} teamId={String(game.team2Id)} showSubLabel />
        </div>
      </div>
    </>
  );
}
